/* How long ago something happened, in words.
 *
 * The audience is Alyssa and Jason glancing at the top of the Intake Queue to answer one question:
 * is this list current? "3 hours ago" answers it; "2026-08-30T20:48:49.719Z" does not. The exact
 * stamp is still available — it goes in the `title`, so hovering gives the precise answer without
 * spending a line of the header on it.
 */

/** Parse a stamp that may be ISO or PocketBase's space-separated form. `null` when unusable. */
function parse(value: string | null | undefined): Date | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  /* The space form is not reliably parseable — V8 takes it, Safari has historically returned
     Invalid Date. The API normalises before sending, so this is belt-and-braces for a stamp that
     reached the browser by another route. */
  const ms = Date.parse(raw.replace(' ', 'T'));
  return Number.isFinite(ms) ? new Date(ms) : null;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'} ago`;

/**
 * "just now" · "9 minutes ago" · "3 hours ago" · "yesterday" · "4 days ago" · "on 24 Aug 2026"
 *
 * Returns '' for anything unparseable, so a caller can render nothing rather than "NaN ago".
 */
export function timeAgo(value: string | null | undefined, now: Date = new Date()): string {
  const then = parse(value);
  if (!then) return '';

  const delta = now.getTime() - then.getTime();

  /* A stamp in the future is a clock difference between this browser and the Mac Mini, not a
     prediction. "in 3 minutes" at the top of the page would read as a bug; "just now" is both
     truer and calmer. */
  if (delta < MINUTE) return 'just now';
  if (delta < HOUR) return plural(Math.floor(delta / MINUTE), 'minute');
  if (delta < DAY) return plural(Math.floor(delta / HOUR), 'hour');
  if (delta < 2 * DAY) return 'yesterday';
  if (delta < 7 * DAY) return plural(Math.floor(delta / DAY), 'day');

  return `on ${then.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/** The precise stamp, for a tooltip. '' when unparseable. */
export function exactTime(value: string | null | undefined): string {
  const then = parse(value);
  if (!then) return '';
  return then.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Just the calendar date, `YYYY-MM-DD`. '' when unparseable.
 *
 * ⚠️ Goes through the same `parse` as everything else in this file, and that matters: PocketBase
 * writes `created` as "2026-09-01 11:10:25.897Z" — a SPACE, not a T — which `new Date()` is not
 * required to accept and which Safari has historically rejected outright. A fourth hand-rolled
 * date parse in the card would have been the place that broke on somebody else's browser.
 */
export function isoDate(value: string | null | undefined): string {
  const then = parse(value);
  return then ? then.toISOString().slice(0, 10) : '';
}
